import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, CodeEditorField, Tooltip, Spinner, Alert } from '@dopomogai/ui';
import { Code, Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { useActiveWidgetForAgent } from '@dopomogai/agent-kit';
import { toast } from '@dopomogai/ui';
import type { UUID } from '@dopomogai/types';

interface DeploymentCardProps {
  agentId: UUID;
}

export const DeploymentCard: React.FC<DeploymentCardProps> = ({ agentId }) => {
  const navigate = useNavigate();
  const { template, activeVersion, embedScript, isLoading, error } = useActiveWidgetForAgent(agentId);

  const [copySuccess, setCopySuccess] = React.useState(false);

  React.useEffect(() => {
    if (!copySuccess) return;
    const timer = window.setTimeout(() => setCopySuccess(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copySuccess]);

  const handleCopyCode = async () => {
    if (!embedScript) return;
    try {
      await navigator.clipboard.writeText(embedScript);
      setCopySuccess(true);
      toast.success('Embed script copied.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to copy embed script.');
    }
  };

  const handleOpenChannels = () => {
    navigate(`/app/agents/${agentId}/channels`);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center h-40">
          <Spinner text="Loading web widget..." />
        </div>
      );
    }

    if (error) {
      return <Alert type="error" title="Error" message={error.message} />;
    }

    // No active version yet
    if (!activeVersion) {
      return (
        <>
          <div className="py-4 text-sm text-slate-600">
            No active version found. Configure the Web Widget from Channels to generate the embed snippet.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Tooltip text="Open Channels to configure domains, colors, and messaging">
              <Button
                text="Manage web widget"
                icon={<ExternalLink size={16} />}
                onClick={handleOpenChannels}
                variant="outline"
              />
            </Tooltip>
          </div>
        </>
      );
    }

    // Active version present: show info first
    return (
      <>
        <div className="text-sm text-slate-700">
          <div>
            Active version: <span className="font-semibold">{activeVersion.version_name}</span>
          </div>
          <div className="text-xs text-slate-500">
            Template: {template?.name ?? template?.id ?? 'Unknown'} • Created{' '}
            {new Date(activeVersion.created_at).toLocaleString()}
          </div>
        </div>

        {embedScript ? (
          <>
            <div className="mt-3">
              <CodeEditorField value={embedScript} onChange={() => {}} language="html" readOnly />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                text={copySuccess ? 'Copied!' : 'Copy script'}
                icon={copySuccess ? <CheckCircle size={16} /> : <Copy size={16} />}
                onClick={handleCopyCode}
                variant="secondary"
              />
              <Tooltip text="Open Channels to configure domains, colors, and messaging">
                <Button
                  text="Manage web widget"
                  icon={<ExternalLink size={16} />}
                  onClick={handleOpenChannels}
                  variant="outline"
                />
              </Tooltip>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between gap-3 mt-3">
            <Alert type="warning" message="Embed code is not available at the moment. Please open Channels and refresh." />
            <Button
              text="Manage web widget"
              icon={<ExternalLink size={16} />}
              onClick={handleOpenChannels}
              variant="outline"
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Card title="Web Widget" icon={<Code size={18} />} headerBgColor="bg-rose-100">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Copy the hosted script or update its configuration from the Channels page.
        </p>
        {renderContent()}
      </div>
    </Card>
  );
};